/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    IAddToOrderTemplateDialogResources,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources,
    ILinesAddedToOrderTemplateDialogResources,
    IOrderTemplateNameDialogResources,
    IWishlistActionSuccessResult,
    PromoCodeComponent,
    SaveCartToOrderTemplateComponent
} from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { getCartState, getCheckoutState, ICartActionResult, ICartState, ICheckoutState } from '@msdyn365-commerce/global-state';
import {
    AsyncResult,
    CartLine,
    ChannelDeliveryOptionConfiguration,
    // ChannelDeliveryOptionConfiguration,
    CustomerBalances,
    FilterDeliveryModeOption,
    LineDataValidationFailure,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    Address,
    DeliveryPreferenceType,
    LineDeliverySpecification,
    ReleasedProductType
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { cartLineQuantityUpdatedAsync, validateCartAsync } from '@msdyn365-commerce-modules/cart-utilities';
import { IInvoiceSummaryLines, InvoiceSummary } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { getBMAStockistAsync, getRequestOrderAsync } from '../../dataActions/DataActionExtension.g';
import { IOrderSummaryLines, OrderSummary } from '@msdyn365-commerce-modules/order-summary-utilities';
import {
    ActiveCartProductsInput,
    ArrayExtensions,
    getActiveCartProductsAction,
    getFallbackImageUrl,
    ObjectExtensions,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import {
    copyAsync,
    getDeliveryOptionsAsync,
    updateCartLinesAsync,
    updateLineDeliverySpecificationsAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    Alert,
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant,
    Waiting
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { IAaaCartData } from './aaa_cart.data';
import { IAaa_cartProps, IAaa_cartResources } from './aaa_cart.props.autogenerated';
import { CartLineGroupItems } from './components/aaa_cart-line-groups';
import { CartLineItems, ICartlinesViewProps } from './components/aaa_cart-line-items';
import { cartPromotions, ICartLineItemViewProps } from './components/aaa_cart-line-items-promotion';
import { getPromotionData, IPromotionData } from './utils/get-promotion-in-aaa_cart';
import axios from 'axios';
import moment from 'moment';
import placeOrder from '../aaa_checkout/methods/place-order';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import AlertComponent from '../aaa_checkout/components/alert';

export interface ICartViewProps extends IAaa_cartProps<IAaaCartData> {
    className: string;
    title?: React.ReactNode;
    CartlinesWrapper: INodeProps;
    cartlines?: ICartlinesViewProps[];
    cartLinesGroup?: ICartlinesViewProps[][];
    cart: ICartState | undefined;
    orderSummaryHeading: React.ReactNode;
    orderSummaryLineitems?: IOrderSummaryLines;
    invoiceSummaryLineitems?: IInvoiceSummaryLines;
    OrderSummaryWrapper: INodeProps;
    OrderSummaryItems: INodeProps;
    OrderSummaryErrors?: IOrderSummaryErrors;
    checkoutAsGuestButton: React.ReactNode;
    checkoutAsSignInUserButton: React.ReactNode;
    placeOrder: React.ReactNode;
    storeSelector?: React.ReactNode;
    backToShoppingButton: React.ReactNode;
    waitingComponent: React.ReactNode;
    promotionOptions?: ICartLineItemViewProps;
    promoCode: React.ReactNode;
    cartLoadingStatus: string;
    cartDataResult: boolean;
    multiplePickUpEnabled?: boolean;
    createTemplateFromCartButton: React.ReactNode;
    alert?: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    removeItemClickHandler(cartlineToRemove: CartLine): void;
    moveToWishlistSuccessHandler(result: IWishlistActionSuccessResult, cartlineId: CartLine): void;
}

export interface IOrderSummaryErrors {
    Wrapper: INodeProps;

    header: React.ReactNode;
    errors: React.ReactNode[];
}

interface ICartComponentState {
    showPromotionDialog: boolean;
    cartlinesErrors: LineDataValidationFailure[];
    promotionData: IPromotionData;
    products: SimpleProduct[];
    isCartValid: boolean;
    cartLinesExtraInfo: any;
    errorMessage: any;
}

/**
 *
 * Cart component.
 * @extends {React.PureComponent<IAaa_cartProps<IAaaCartData>>}
 */
@observer
class Cart extends React.Component<IAaa_cartProps<IAaaCartData>> {
    public state: ICartComponentState = {
        cartlinesErrors: [],
        showPromotionDialog: false,
        promotionData: {},
        products: [],
        isCartValid: true,
        cartLinesExtraInfo: [],
        errorMessage: ''
    };

    @computed public get isPaymentVerificationRedirection(): boolean {
        const { requestFormData, query } = this.props.context.request;
        return !!(requestFormData || (query && query.pv === '1'));
    }

    private readonly telemetryContent: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    private isQuantityLimitsFeatureEnabled: boolean = false;

    private _isMounted: boolean = false;

    private readonly promotionPopupId: string = 'cart-promotion-popup';

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    private channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    constructor(props: IAaa_cartProps<IAaaCartData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
        this.promotionLinkClick = this.promotionLinkClick.bind(this);
        this.promotionCloseButtonclick = this.promotionCloseButtonclick.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        this._isMounted = true;
        const {
            config,
            context: { actionContext }
        } = this.props;

        this.isQuantityLimitsFeatureEnabled = await this._getOrderQuantityLimitsFeatureFlag();
        if (this.isQuantityLimitsFeatureEnabled) {
            await this._validateCart();
        }

        try {
            const products = await this.props.data.products;
            if (this._isMounted) {
                this.setState({ products });
            }
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get cart');
        }

        if (config.showAvailablePromotions) {
            if (this._isMounted && this.props.data.cart.result?.cart.CartLines) {
                this.setState({ promotionData: await getPromotionData(actionContext) });
            }

            reaction(
                () => this.props.data.cart.result?.cart.CartLines,
                async () => {
                    if (this._isMounted) {
                        this.setState({ promotionData: await getPromotionData(actionContext) });
                    }
                }
            );
        }
        if (this._isMounted && this.props.data.cart.result?.cart.CartLines) {
            const requestOrders: any = await this.getRequestOrderList();
            const cartLines: any = this.props.data.cart.result?.cart.CartLines;
            const updatedCartLines: any = [];
            const jwellerInfo = await getBMAStockistAsync({ callerContext: this.props.context.actionContext, queryResultSettings: {} });
            let translationInfo = await axios.get(
                `${this.props.app.config.bmaBaseUrl}${this.props.app.config.bmaTranslationMessageEndPoint}`
            );
            translationInfo = translationInfo.data;
            for (const cartLine of cartLines) {
                const updateCartItem: any = {
                    lineId: cartLine.LineId,
                    ImageUrl: '',
                    CustomerInfo: {},
                    SnapShotId: '',
                    ItemInfo: { price: 0 }
                };
                if (cartLine.ExtensionProperties && cartLine.ExtensionProperties.length > 0) {
                    const snapShotProperty = cartLine.ExtensionProperties.find((e: any) => {
                        return e.Key === 'AAA_SNAPSHOTID' && e.Value && e.Value.StringValue;
                    });
                    if (snapShotProperty && snapShotProperty.Value && snapShotProperty.Value.StringValue) {
                        const bmaImageWithFormatEndPoint = this.props.app.config.bmaImageEndPoint.replace(
                            '#snapshotId',
                            snapShotProperty.Value.StringValue
                        );
                        const image = await axios.get(`${this.props.app.config.bmaBaseUrl}${bmaImageWithFormatEndPoint}`);
                        updateCartItem.ImageUrl = image.data[0];
                        updateCartItem.SnapShotId = snapShotProperty.Value.StringValue;
                        const bmaReviewEndPoint = this.props.app.config.bmaReviewEndPoint.replace(
                            '#snapshotId',
                            snapShotProperty.Value.StringValue
                        );
                        const review = await axios.get(`${this.props.app.config.bmaBaseUrl}${bmaReviewEndPoint}`);

                        let bmaPriceEndPoint = this.props.app.config.bmaPriceEndPoint.replace(
                            '#snapshotId',
                            snapShotProperty.Value.StringValue
                        );

                        bmaPriceEndPoint = bmaPriceEndPoint.replace('#jewellerId', jwellerInfo.BMARetailStockist);
                        const price = await axios.get(`${this.props.app.config.bmaBaseUrl}${bmaPriceEndPoint}`);
                        const ringsInfo = price.data.rings;
                        updateCartItem.ItemInfo.review = review.data;
                        updateCartItem.ItemInfo.snapShotId = snapShotProperty.Value.StringValue;
                        updateCartItem.ItemInfo.key = snapShotProperty.Value.StringValue;
                        updateCartItem.ItemInfo.review = review.data;
                        updateCartItem.ItemInfo.recId = cartLine.RecordId;
                        updateCartItem.ItemInfo.fallbackImage = getFallbackImageUrl(
                            cartLine.ItemId,
                            this.props.context.actionContext.requestContext.apiSettings
                        );
                        updateCartItem.ItemInfo.rings = [];
                        updateCartItem.ItemInfo.totalPrice = price.data.sum.vk;
                        for (const ring of ringsInfo) {
                            const ringReview = updateCartItem.ItemInfo.review.find((r: any) => {
                                return r.ringId === ring.id;
                            });
                            let height = '5mm';
                            let width = '1.4mm';
                            if (ringReview.properties && ringReview.properties.profile) {
                                if (ringReview.properties.profile.width) {
                                    width = `${ringReview.properties.profile.width.value} ${ringReview.properties.profile.width.type}`;
                                }
                                if (ringReview.properties.profile.height) {
                                    height = `${ringReview.properties.profile.height.value} ${ringReview.properties.profile.height.type}`;
                                }
                            }
                            let diamondCount = null;
                            diamondCount =
                                ringReview.properties.stone.preset.length > 0 &&
                                ringReview.properties.stone.preset[0].id !== 'none' &&
                                ringReview.properties.stone.quantity.reduce(function(sum: any, current: any) {
                                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                    return sum + current.count;
                                }, 0);
                            const ringSizePi = ringReview.properties.ring.size.value / 3.14159265;
                            const ringFineness = ringReview.properties.disc.fineness.map((fineness: any) => {
                                return `${translationInfo[fineness.label]}`;
                            });
                            const ringColor = ringReview.properties.disc.color.map((color: any) => {
                                return `${translationInfo[color.label]}`;
                            });
                            const ringPolish = ringReview.properties.disc.surface.map((polish: any) => {
                                return `${translationInfo[polish.label]}`;
                            });
                            const ringFinenessColorPolish = ringColor.map((color: any, index: number) => {
                                return `${ringFineness[index]} ${color} ${ringPolish[index]}`;
                            });
                            const ringInfo: any = {
                                ringId: ring.id,
                                ringName: ringReview.name,
                                alloy: `${translationInfo[ringReview.properties.disc.fineness[0].label]}
                                ${''}${translationInfo[ringReview.properties.disc.color[0].label]}`,
                                sizeLabel: ringReview.properties.ring.size.label,
                                sizeValue:
                                    Math.round(
                                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                        (ringSizePi + Number.EPSILON) * 100
                                    ) / 100,
                                specifications: diamondCount
                                    ? `${diamondCount} Diamonds, ${width} Wide, ${height} thick`
                                    : `${width} Wide, ${height} thick`,
                                engraving: ringReview.properties.eSpot.text.inner,
                                ringFineness: ringFineness,
                                ringColor: ringColor,
                                ringPolish: ringPolish,
                                ringFinenessColorPolishInfo: ringFinenessColorPolish
                            };
                            ringInfo.priceAmount = ring.vk;
                            updateCartItem.ItemInfo.rings.push(ringInfo);
                        }
                    }
                    const requestOrderIdProperty = cartLine.ExtensionProperties.find((e: any) => {
                        return e.Key === 'AAA_REQUESTORDERID' && e.Value && e.Value.StringValue;
                    });
                    const requestOrderLineNumProperty = cartLine.ExtensionProperties.find((e: any) => {
                        return e.Key === 'AAA_REQUESTORDERLINENUM' && e.Value && e.Value.DecimalValue;
                    });
                    if (
                        requestOrderIdProperty &&
                        requestOrderIdProperty.Value &&
                        requestOrderIdProperty.Value.StringValue &&
                        requestOrderLineNumProperty &&
                        requestOrderLineNumProperty.Value &&
                        requestOrderLineNumProperty.Value.DecimalValue
                    ) {
                        const requestOrder = requestOrders.RequestOrder.find((requestOrder: any) => {
                            return (
                                requestOrder.RequestOrderId === requestOrderIdProperty.Value.StringValue &&
                                requestOrderLineNumProperty.Value.DecimalValue === requestOrder.LineNum
                            );
                        });
                        if (requestOrder) {
                            updateCartItem.CustomerInfo.b2cEmailId = requestOrder.B2CEmailId;
                            updateCartItem.CustomerInfo.b2cCustomerName = requestOrder.b2CCustomerName;
                            updateCartItem.CustomerInfo.b2cPhone = requestOrder.B2CPhone;
                        }
                    }
                    if (
                        updateCartItem.CustomerInfo && // 👈 null and undefined check
                        Object.keys(updateCartItem.CustomerInfo).length === 0 &&
                        Object.getPrototypeOf(updateCartItem.CustomerInfo) === Object.prototype
                    ) {
                        delete updateCartItem.CustomerInfo;
                    }
                    updatedCartLines.push(updateCartItem);
                }
            }
            this.setState({ cartLinesExtraInfo: updatedCartLines });
        }
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
    }
    private async getRequestOrderList() {
        const response: any = await getRequestOrderAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            ''
        );
        return response;
    }
    public shouldComponentUpdate(nextProps: IAaa_cartProps<IAaaCartData>, nextState: ICartComponentState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    private _navigateToCheckout(navigatedUrl: string) {
        const cart: any = this.props.data.cart.result?.cart;
        updateCartLinesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            cart.Id,
            cart.CartLines,
            cart.Version
        )
            .then(cartActionResult => {
                window.location.href = navigatedUrl;
            })
            .catch(() => {
                return false;
            });
    }

    private readonly updateCartLineEmailAddress = async (newGuestCheckoutEmail: string): Promise<number | undefined> => {
        const emailDeliveryModeCode = this.props.context.request.channel?.EmailDeliveryModeCode;

        const emailDeliveryCartLines = this.props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(
            cartLine => cartLine.DeliveryMode && cartLine.DeliveryMode !== '' && cartLine.DeliveryMode === emailDeliveryModeCode
        );

        let updatedCartVersion: number | undefined;
        if (emailDeliveryCartLines && emailDeliveryCartLines.length > 0) {
            const lineDeliverySpecifications: LineDeliverySpecification[] = emailDeliveryCartLines.map(line => {
                return {
                    LineId: line.LineId,
                    DeliverySpecification: {
                        DeliveryModeId: emailDeliveryModeCode,
                        DeliveryPreferenceTypeValue: DeliveryPreferenceType.ElectronicDelivery,
                        ElectronicDeliveryEmailAddress: newGuestCheckoutEmail
                    }
                };
            });

            await updateLineDeliverySpecificationsAsync(
                { callerContext: this.props.context.actionContext },
                (await this.props.data.checkout).checkoutCart.cart.Id,
                lineDeliverySpecifications
            )
                .then(updatedCart => {
                    updatedCartVersion = updatedCart.Version;
                })
                .catch(error => {
                    this.props.context.actionContext.telemetry.exception(error);
                    throw new Error('[updateLineDeliverySpecificationsAsync] Unable to update electronic delivery email address in cart');
                });
        }

        return updatedCartVersion;
    };
    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
        }
    };
    private readonly copyCart = async (): Promise<void> => {
        const currentCartState = await getCartState(this.props.context.actionContext);
        const activeCart = currentCartState.cart;
        if (activeCart && Object.keys(activeCart).length > 0) {
            const checkoutCart = await copyAsync({ callerContext: this.props.context.actionContext }, activeCart.Id, 2);
            if (checkoutCart) {
                return Array.isArray(checkoutCart) ? checkoutCart[0] : checkoutCart;
            }
        } else {
            this.props.context.actionContext.trace("[get-checkout-cart] Can't create a checkout cart since active cart is empty");
        }
    };
    private readonly updateEmail = async (): Promise<void> => {
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        const customerAccountInformation = this.props.data.customerInformation?.result;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;
        let updateEmail: string = '';

        // if obo enabled and profile selected, we need to send the order status to obo user not store manager.
        if (Msdyn365.isOboRequest(this.props.context.request)) {
            updateEmail =
                customerAccountInformation && customerAccountInformation.Email !== undefined ? customerAccountInformation.Email : '';
        } else {
            updateEmail =
                (this.props.context &&
                    this.props.context.request &&
                    this.props.context.request.user &&
                    this.props.context.request.user.isAuthenticated &&
                    this.props.context.request.user.emailAddress) ||
                '';
        }

        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied && !StringExtensions.isNullOrEmpty(checkoutState.emailAddressFromExpressCheckout)
                  ? checkoutState.emailAddressFromExpressCheckout
                  : undefined) ??
              updateEmail;

        if (email) {
            await this.updateGuestProfile(email);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }
    };
    @computed get maxPaymentAmount(): number {
        const cart = this.props.data.checkout.result ? this.props.data.checkout.result.checkoutCart.cart : undefined;
        if (!cart) {
            return 0;
        }

        // Use customer account after gift card and loyalty.
        const amountDue = Math.max(0, (cart.TotalAmount || 0) - this.getLoyaltyAmount - this.getGiftCardAmount);

        // If the user has a mandatory credit limit, then the max amount must not be more than that
        if (this.props.data.customerInformation?.result?.MandatoryCreditLimit) {
            return Math.min(this.getAvailableCredit(this.props.data.creditBalances?.result), amountDue);
        }
        return amountDue;
    }
    private readonly getAvailableCredit = (creditBalances: CustomerBalances | undefined) => {
        let availableCredit = 0;

        if (!creditBalances) {
            return 0;
        }

        if (creditBalances.InvoiceAccountCreditLimit === 0) {
            availableCredit = creditBalances.CreditLimit - creditBalances.Balance - creditBalances.PendingBalance;
        } else {
            availableCredit =
                creditBalances.InvoiceAccountCreditLimit -
                creditBalances.InvoiceAccountBalance -
                creditBalances.InvoiceAccountPendingBalance;
        }

        return availableCredit;
    };
    @computed get getLoyaltyAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.loyaltyAmount) {
            return 0;
        }
        return checkoutState.loyaltyAmount;
    }
    @computed get getGiftCardAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.giftCards || checkoutState.giftCards.length === 0) {
            return 0;
        }

        let giftCardAmount: number = 0;
        checkoutState.giftCards.forEach(giftCard => {
            giftCardAmount += giftCard.Balance || 0;
        });

        return giftCardAmount;
    }
    private readonly addPayment = async (amount: any): Promise<void> => {
        const checkoutState = await getCheckoutState(this.props.context.actionContext);

        if (!checkoutState) {
            this.props.context.telemetry.error('checkout state not found');
            return;
        }
        await checkoutState.updateCustomerAccountAmount({ newAmount: amount });
        this.props.context.telemetry.information('customer account payment amount updated');
    };

    private readonly updateDeliveryOption = async (): Promise<void> => {
        await this.loadDeliveryOptions();
    };
    private readonly getCartLinesForShipping = (): CartLine[] => {
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        this.retailMultiplePickUpOptionEnabled = this.props.data.featureState?.result?.find(
            feature => feature.Name === multiplePickupStoreSwitchName
        )?.IsEnabled;
        return this.retailMultiplePickUpOptionEnabled
            ? ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            this.channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              )
            : ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              );
    };
    private readonly canShip = (): boolean => {
        const hasInvoice = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        return !hasInvoice && this.getCartLinesForShipping().length > 0;
    };
    private readonly updateCartDeliverySpecification = async (deliveryModeId: string): Promise<void> => {
        if (deliveryModeId.trim() === '') {
            return;
        }

        // const { resources } = this.props;
        const checkoutState = this.props.data.checkout.result as ICheckoutState;
        const updateCartDeliverySpecificationInput = {
            deliveryModeId,
            shippingAddress: checkoutState.shippingAddress,
            channelDeliveryOptionConfig: this.retailMultiplePickUpOptionEnabled ? this.channelDeliveryOptionConfig : undefined
        };

        await checkoutState.checkoutCart
            .updateCartDeliverySpecification(updateCartDeliverySpecificationInput)
            .then(response => {
                if (response.status === 'FAILED') {
                    // const errorMessage = `${response.errorDetails?.LocalizedMessage}`;
                    // this.setError(StringExtensions.isNullOrEmpty(errorMessage) ? resources.genericErrorMessage : errorMessage);
                }
            })
            .catch(() => {
                // this.setError(resources.genericErrorMessage);
            });
    };
    private readonly getDeliveryOptions = async (shippingAddress: Address): Promise<any> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context } = this.props;
        const cartState = this.props.data.checkout.result?.checkoutCart;
        const cartId: any = (cartState.cart as any).Id;

        if (context) {
            let deliveryOptions: any = [];
            let selectedItemId = '';
            try {
                let originalDeliveryMode: string = '';
                const shippingCartLines = this.getCartLinesForShipping();
                const hasShippingDeliveryLines = shippingCartLines && shippingCartLines.length > 0 && shippingCartLines[0].DeliveryMode;
                if (hasShippingDeliveryLines) {
                    // First store the delivery mode, so that we can restore the shipping information.
                    originalDeliveryMode = shippingCartLines[0].DeliveryMode || '';

                    // Clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                    const cart = cartState.cart;
                    await cartState.clearCartLinesDeliveryInformation({
                        cart,
                        channelDeliveryOptionConfiguration: this.channelDeliveryOptionConfig
                    });
                }

                // TODO: Check if we can move this getDeliveryOptions to checkout state
                const deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                const response = await getDeliveryOptionsAsync(
                    {
                        callerContext: context.actionContext,
                        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context.actionContext)
                    },
                    cartId,
                    shippingAddress,
                    [],
                    deliveryOptionFilter,
                    true
                );

                if (hasShippingDeliveryLines) {
                    await this.updateCartDeliverySpecification(originalDeliveryMode);
                }

                if (response && response.length > 0) {
                    deliveryOptions = response;
                }
            } catch {
                // this.setError(resources.genericErrorMessage);
            }

            if (deliveryOptions.length === 0) {
                // this.setError(resources.deliveryOptionsNotFound);
            } else {
                selectedItemId = deliveryOptions.length > 0 ? deliveryOptions[0].Code || '' : ''; // By default, select first delivery

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: true });
                return selectedItemId;
            }
        }
    };
    private readonly loadDeliveryOptions = async (): Promise<void> => {
        const shippingAddress = this.props.data.checkout.result?.shippingAddress;
        let selectedItemId = '';
        const isSelectDefaultOptionEnabled = true;
        if (this.canShip() && shippingAddress) {
            if (
                !this.props.data.checkout.result?.isPaymentVerificationRedirection ||
                isSelectDefaultOptionEnabled ||
                this.props.data.checkout.result?.isExpressCheckoutApplied
            ) {
                selectedItemId = await this.getDeliveryOptions(shippingAddress);
            }
            if (selectedItemId && (isSelectDefaultOptionEnabled || this.props.data.checkout.result?.isExpressCheckoutApplied)) {
                await this.updateCartDeliverySpecification(selectedItemId);
            }
        }
    };
    private readonly _getDefaultAddress = (): Address | undefined => {
        const customerAddresses = this.props.data.address.result ?? [];
        if (customerAddresses) {
            const primaryAddress = customerAddresses.find((address: Address) => address.IsPrimary);
            return primaryAddress || (customerAddresses.length > 0 ? customerAddresses[0] : undefined);
        }
        return undefined;
    };
    private readonly _setShippingAddress = (address: Address): void => {
        if (this.props.data.checkout.result) {
            const newShippingAddress = { ...address };
            void this.props.data.checkout.result.updateShippingAddress({ newShippingAddress });
            this.props.data.checkout.result.checkoutCart.updateShippingAddress({ newShippingAddress }).catch(error => {
                this.props.telemetry.error(error);
            });
        }
    };
    private readonly updateShippingAddress = async (): Promise<void> => {
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        let defaultAddress;

        if (checkoutState?.checkoutCart.cart.ShippingAddress) {
            defaultAddress = checkoutState.checkoutCart.cart.ShippingAddress;
        } else if (this._getDefaultAddress()) {
            defaultAddress = this._getDefaultAddress();
        } else {
            defaultAddress = checkoutState?.isExpressCheckoutApplied ? checkoutState.shippingAddressFromExpressCheckout : undefined;
        }

        if (defaultAddress) {
            this._setShippingAddress(defaultAddress);
            checkoutState?.updateHasShippingAddress({ newHasShippingAddress: true });
        }
    };
    private updateCartLinesAndPlaceOrder() {
        const cart: any = this.props.data.cart.result?.cart;
        void updateCartLinesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            cart.Id,
            cart.CartLines,
            cart.Version
        ).then(cartActionResult => {
            void this._placeOrder();
        });
    }
    private async updateCheckoutCartLines() {
        const cart: any = this.props.data.cart.result?.cart;
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        await updateCartLinesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            checkoutState.checkoutCart.cart.Id,
            cart.CartLines,
            checkoutState.checkoutCart.cart.Version
        );
    }
    private async _placeOrder() {
        const {
            resources: {
                genericErrorMessage,
                invalidCartVersionErrorMessage,
                customerCreditLimitExceededErrorMessage,
                customerCreditLimitExhaustedErrorMessage
            },
            context: { actionContext }
        } = this.props;
        // update checkout cart
        await this.updateCheckoutCartLines();
        //copy cart
        await this.copyCart();
        // update email in checkout cart
        await this.updateEmail();
        // update shipping address  in checkout cart
        await this.updateShippingAddress();
        // update delivery option  in checkout cart
        await this.updateDeliveryOption();
        // update amount in checkout cart
        const maxAmount = this.maxPaymentAmount;
        await this.addPayment(maxAmount);
        const updatedCartVersion = await this.updateCartLineEmailAddress(this.props.data.checkout.result?.guestCheckoutEmail || '');
        await placeOrder(
            actionContext,
            this.props.data.checkout.result,
            this.props.data.products.result,
            true,
            updatedCartVersion,
            this.isPaymentVerificationRedirection
        ).catch(error => {
            let errorMessage = genericErrorMessage;
            if (error && error.message && error.message.indexOf('customerCreditLimitExhaustedErrorMessage') > -1) {
                errorMessage = customerCreditLimitExhaustedErrorMessage;
            }
            if (error && error.message && error.message.indexOf('customerCreditLimitExceededErrorMessage') > -1) {
                errorMessage = customerCreditLimitExceededErrorMessage;
            }

            if (error && error.name === 'Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion') {
                errorMessage = invalidCartVersionErrorMessage;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- unknown error type.
            if (
                this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout &&
                error.data?.AdditionalContext &&
                !this.isPaymentVerificationRedirection
            ) {
                return;
            }

            this.setState({
                errorMessage
            });
            this.props.telemetry.exception(error);
        });
    }
    public render(): JSX.Element | null {
        const {
            data: {
                cart: { result: cart },
                products: { result: products }
            },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            resources
        } = this.props;

        const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const backToShoppingUrl = getUrlSync('home', this.props.context.actionContext) || '';
        const checkoutURL = this._getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        const cartLines = this._getCartLines();
        const hasError = !this.state.isCartValid || (cartLines ? cartLines.some(cartline => cartline.hasError) : false);
        const RetailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );
        const multiplePickUpEnabled = !cart?.hasInvoiceLine && RetailMultiplePickupFeatureState?.IsEnabled;
        const cartLinesGroup = multiplePickUpEnabled ? this._getCartLinesGroup() : null;

        this.payLoad.contentAction.etext = TelemetryConstant.Checkout;
        const checkoutBySignInAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.GuestCheckout;
        const checkoutByGuestAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.BackToShopping;
        const backtoShoppingAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        const viewProps = {
            ...this.props,
            className: classnames('ms-cart'),
            title: <h1 className='msc-cart__heading'>{resources.cartTitle}</h1>,
            cart: this.props.data.cart.result,
            CartlinesWrapper: { className: 'msc-cartline-wraper' },
            OrderSummaryWrapper: { className: cart?.hasInvoiceLine ? 'msc-invoice-summary-wrapper' : 'msc-order-summary-wrapper' },
            OrderSummaryItems: { className: cart?.hasInvoiceLine ? 'msc-invoice-summary__items' : 'msc-order-summary__items' },
            cartlines: cartLines,
            cartLinesGroup,
            promoCode: this._getPromoCode(),
            promotionOptions: this._getPromotions(),
            context: this.props.context,
            typeName: this.props.typeName,
            telemetry: this.props.telemetry,
            id: this.props.id,
            multiplePickUpEnabled,
            removePromoAriaLabelFormat: resources.removePromoAriaLabelFormat,
            orderSummaryHeading: ArrayExtensions.hasElements(cart?.cart.CartLines) ? (
                cart?.hasInvoiceLine ? (
                    <h2 className='msc-invoice-summary__heading'>{resources.invoiceSummaryTitle}</h2>
                ) : (
                    <h2 className='msc-order-summary__heading'>{resources.orderSummaryTitle}</h2>
                )
            ) : null,
            orderSummaryLineitems: this._getOrderSummaryLineItems(),
            invoiceSummaryLineitems: this._getInvoiceSummaryLineItems(),
            checkoutAsSignInUserButton: !isCartEmpty ? (
                <Button
                    disabled={hasError}
                    className='msc-cart__btn-checkout'
                    // href={checkoutURL}
                    onClick={() => this._navigateToCheckout(checkoutURL)}
                    title={resources.checkoutButtonTitle}
                    {...checkoutBySignInAttributes}
                >
                    {resources.checkoutButtonTitle}
                </Button>
            ) : null,
            placeOrder: !isCartEmpty ? (
                <Button
                    disabled={hasError}
                    className='msc-cart__btn-checkout'
                    // href={checkoutURL}
                    onClick={() => this.updateCartLinesAndPlaceOrder()}
                    title={resources.placeOrderButtonTitle}
                    {...checkoutBySignInAttributes}
                >
                    {resources.placeOrderButtonTitle}
                </Button>
            ) : null,
            checkoutAsGuestButton:
                !isCartEmpty && !isAuthenticated ? (
                    <Button
                        disabled={hasError}
                        className='msc-cart__btn-guestcheckout'
                        // href={guestCheckoutUrl}
                        onClick={() => this._navigateToCheckout(guestCheckoutUrl)}
                        title={resources.guestCheckoutButtonTitle}
                        {...checkoutByGuestAttributes}
                    >
                        {resources.guestCheckoutButtonTitle}
                    </Button>
                ) : null,
            backToShoppingButton: (
                <Button
                    className='msc-cart__btn-backtoshopping'
                    {...backtoShoppingAttributes}
                    // href={backToShoppingUrl}
                    onClick={() => this._navigateToCheckout(backToShoppingUrl)}
                    title={resources.backToShoppingButtonTitle}
                >
                    {resources.backToShoppingButtonTitle}
                </Button>
            ),
            createTemplateFromCartButton:
                this.props.config.isCreateTemplateFromCartButtonAllow && this._displayAddCartToTemplate() && products ? (
                    <SaveCartToOrderTemplateComponent
                        addToOrderTemplateDialogResources={this._generateAddToOrderTemplateDialogResources(resources)}
                        createOrderTemplateDialogResources={this._generateOrderTemplateNameDialogResources(resources)}
                        linesAddedToOrderTemplateDialogResources={this._generateLinesAddedToOrderTemplateDialogResources(resources)}
                        createOrderTemplateFromCartButton={resources.createOrderTemplateFromCartButton}
                        createOrderTemplateFromCartButtonTooltip={resources.createOrderTemplateFromCartButtonTooltip}
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        data={{
                            orderTemplates: this.props.data.orderTemplates.result!,
                            cartLines: this.props.data.cart.result!.cart.CartLines!,
                            customerInformation: this.props.data.customerInformation.result
                        }}
                    />
                ) : null,
            storeSelector:
                this.props.slots.storeSelector && this.props.slots.storeSelector.length > 0 ? this.props.slots.storeSelector[0] : null,
            removeItemClickHandler: this._removeItemFromCartHandler,
            moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
            waitingComponent: <Waiting className='msc-waiting-circular msc-waiting-lg' />,
            cartLoadingStatus: this.props.data.cart.status === 'FAILED' ? this.props.resources.cartLoadingErrorMessage : null,
            cartDataResult: this.props.data.cart.result && this.props.data.cart.result.isEmpty,
            OrderSummaryErrors: this._getOrderSummaryErrors(cartLines),
            telemetryContent: this.telemetryContent,
            alert: this.state.errorMessage && <AlertComponent {...{ message: this.state.errorMessage }} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly promotionLinkClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.setState({
            showPromotionDialog: !this.state.showPromotionDialog
        });
    };

    private readonly promotionCloseButtonclick = () => {
        this._hidePromotionDialog();
    };

    private _hidePromotionDialog(): void {
        this.setState({
            showPromotionDialog: false
        });
    }

    private _displayAddCartToTemplate(): boolean {
        const cart = this.props.data.cart?.result;
        if (ObjectExtensions.isNullOrUndefined(cart) || cart.hasInvoiceLine) {
            return false;
        }

        const RetailB2BEcommerceFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailB2BEcommerceFeature'
        );
        if (
            RetailB2BEcommerceFeatureState?.IsEnabled &&
            this.props.data.customerInformation.result?.IsB2b &&
            this.props.data.orderTemplates.result &&
            ArrayExtensions.hasElements(cart.cart?.CartLines) &&
            this.props.data.customerInformation.result
        ) {
            return true;
        }

        return false;
    }

    private _getPromotions(): React.ReactNode {
        return (
            this.props.config.showAvailablePromotions &&
            cartPromotions({
                id: this.promotionPopupId,
                showPromotionPopup: this.state.showPromotionDialog,
                onSeeAllClick: this.promotionLinkClick,
                onCloseButtonClick: this.promotionCloseButtonclick,
                promotions: this.state.promotionData.promotionOptions,
                resources: {
                    promotionStringHeading: this.props.resources.promotionStringHeading,
                    promotionLinkText: this.props.resources.promotionLinkText,
                    promotionPopupHeading: this.props.resources.promotionPopupHeading,
                    promotionCloseButtonAriaLabel: this.props.resources.promotionCloseButtonAriaLabel
                }
            })
        );
    }

    private _getOrderSummaryErrors(cartLines: ICartlinesViewProps[] | null): IOrderSummaryErrors | undefined {
        const allErrors =
            (cartLines &&
                cartLines
                    .map((cartline: any, index: number) => {
                        // Check if the product is service or not by product type
                        const isServiceItem = cartline.data?.product?.ItemTypeValue === ReleasedProductType.Service;

                        if (!isServiceItem && cartline.error) {
                            const fullError =
                                cartline.data && cartline.data.product?.Name
                                    ? `${cartline.data.product.Name}: ${cartline.error}`
                                    : cartline.error;

                            return (
                                <div className='msc-alert__line' key={index}>
                                    {fullError}
                                </div>
                            );
                        }

                        return null;
                    })
                    .filter((line): line is JSX.Element => !!line)) ||
            [];

        return {
            Wrapper: {
                tag: Alert,
                className: 'msc-alert',
                isOpen: allErrors.length > 0,
                color: 'danger',
                assertive: true,
                'aria-label': this.props.resources.outOfStockErrorMessageHeader
            },
            header: (
                <div className='msc-alert__header' aria-hidden='true'>
                    <span className='msi-exclamation-triangle' />
                    <span>{this.props.resources.outOfStockErrorMessageHeader}</span>
                </div>
            ),
            errors: allErrors
        };
    }

    private _getInvoiceSummaryLineItems(): IInvoiceSummaryLines | undefined {
        const {
            data: {
                cart: { result: cart }
            },
            telemetry,
            resources
        } = this.props;

        return cart?.hasInvoiceLine
            ? InvoiceSummary({
                  orderTotalLabel: resources.totalAmountLabel,
                  invoiceLabel: resources.invoiceLabel,
                  cart: cart.cart,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  telemetry,
                  id: this.props.id
              })
            : undefined;
    }

    private _getOrderSummaryLineItems(): IOrderSummaryLines | undefined {
        const {
            data: {
                cart: { result: cart },
                channelDeliveryOptionConfig
            },
            telemetry,
            resources
        } = this.props;

        const channelConfiguration = this.props.context && this.props.context.request && this.props.context.request.channel;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const featureState = this.props?.data?.featureState;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;
        const isShowOrHideTaxEnabled = this.props.context?.request.app.config?.isEnableShowOrHideSalesTaxECommerceEnabled === true;

        return channelConfiguration && ArrayExtensions.hasElements(cart?.cart.CartLines) && !cart?.hasInvoiceLine
            ? OrderSummary({
                  cart: cart!.cart,
                  subTotalLabel: resources.itemsWithCountLabel,
                  shippingLabel: resources.estimatedShippingLabel,
                  otherChargeLabel: resources.otherCharges,
                  taxLabel: resources.estimatedTaxLabel,
                  orderTotalLabel: resources.orderTotal,
                  totalDiscountsLabel: resources.totalDiscountsLabel,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  toBeCalculatedText: resources.yetToBeCalculatedText,
                  freeText: resources.priceFree,
                  channelConfiguration,
                  retailMultiplePickUpOptionEnabled,
                  channelDeliveryOptionConfig: channelDeliveryOptionConfig?.result,
                  isShowTaxBreakup: isShowOrHideTaxEnabled,
                  telemetry
              })
            : undefined;
    }

    private _getPromoCode(): React.ReactNode {
        const {
            data: {
                cart: { result: cart }
            },
            resources
        } = this.props;

        return (
            cart &&
            this.props.config.isPromoCodeSectionAllow && (
                <PromoCodeComponent
                    cart={cart}
                    promoCodeHeadingText={resources.promoCodeHeadingText}
                    promoPlaceholderText={resources.promoPlaceholderText}
                    promoCodeApplyButtonText={resources.promoCodeApplyButtonText}
                    appliedPromoCodeHeadingText={resources.appliedPromoCodeHeadingText}
                    collapseTimeOut={350}
                    removePromoAriaLabelFormat={resources.removePromoAriaLabelFormat}
                    removePromoText={resources.removePromoText}
                    invalidPromoCodeErrorText={resources.invalidPromoCodeErrorText}
                    failedToAddPromoCodeErrorText={resources.failedToAddPromoCodeErrorText}
                    failedToRemovePromoCodeErrorText={resources.failedToRemovePromoCodeErrorText}
                    duplicatePromoCodeErrorText={resources.duplicatePromotionErrorText}
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{}}
                    telemetryContent={this.telemetryContent}
                />
            )
        );
    }

    private _generateAddToOrderTemplateDialogResources(resources: IAaa_cartResources): IAddToOrderTemplateDialogResources {
        return {
            addToOrderTemplateHeader: resources.addToOrderTemplateHeader,

            noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
            noOrderTemplatesDescription: resources.noOrderTemplatesDescription,

            createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
            createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
            cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,

            selectTemplatesText: resources.selectTemplatesText,
            addToTemplateButtonText: resources.addToTemplateButtonText,
            lineItemsText: resources.lineItemsText
        };
    }

    private _generateItemAddedToOrderTemplateDialogResources(resources: IAaa_cartResources): IItemAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
            itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
            itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
            freePriceText: resources.freePriceText,
            originalPriceText: resources.originalPriceText,
            currentPriceText: resources.currentPriceText
        };
    }

    private _generateLinesAddedToOrderTemplateDialogResources(resources: IAaa_cartResources): ILinesAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            linesAddedToOrderTemplateHeaderItemsOneText: resources.linesAddedToOrderTemplateHeaderItemsOneText,
            linesAddedToOrderTemplateHeaderItemsFormatText: resources.linesAddedToOrderTemplateHeaderItemsFormatText,
            linesAddedToOrderTemplateHeaderLinesOneText: resources.linesAddedToOrderTemplateHeaderLinesOneText,
            linesAddedToOrderTemplateHeaderLinesFormatText: resources.linesAddedToOrderTemplateHeaderLinesFormatText,
            linesAddedToOrderTemplateHeaderMessageText: resources.linesAddedToOrderTemplateHeaderMessageText,
            linesAddedToOrderTemplateCustomPriceAlertMessageText: resources.linesAddedToOrderTemplateCustomPriceAlertMessageText
        };
    }

    private _generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
        resources: IAaa_cartResources
    ): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
        return {
            duplicatedProductsHeader: resources.duplicatedProductsHeader,
            duplicatedProductsDescription: resources.duplicatedProductsDescription,
            updateQuantityButtonText: resources.updateQuantityButtonText,
            cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText
        };
    }

    private _generateOrderTemplateNameDialogResources(resources: IAaa_cartResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            orderTemplateDescription: resources.createOrderTemplateDescription,

            defaultOrderTemplateName: resources.defaultOrderTemplateName,

            orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
        };
    }

    private _getCartLines(): ICartlinesViewProps[] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation }
            },
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);

        const cartlinesErrors: LineDataValidationFailure[] = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }
        return ArrayExtensions.hasElements(cart?.cart.CartLines)
            ? CartLineItems({
                  cartLinesExtraInfo: this.state.cartLinesExtraInfo,
                  cartlines: cart!.cart.CartLines,
                  storeSelectorModuleId: storeSelectorId,
                  cartlinesErrors,
                  products: this.state.products,
                  productAvailabilites: this.props.data.productAvailabilites.result,
                  productDeliveryOptions: this.props.data.deliveryOptions.result,
                  imageSettings,
                  isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                  maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                  shouldIgnoreWarehouse: this.props.context.app.config.warehouseAggregation === 'multiple',
                  gridSettings: this.props.context.request.gridSettings!,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  resources: {
                      sizeString: resources.productDimensionTypeSize,
                      colorString: resources.productDimensionTypeColor,
                      configString: resources.productDimensionTypeConfiguration,
                      styleString: resources.productDimensionTypeStyle,
                      amountString: resources.productDimensionTypeAmount,
                      quantityDisplayString: resources.quantityDisplayText,
                      inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                      decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                      incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                      discountStringText: resources.discountStringText,
                      originalPriceText: resources.originalPriceText,
                      currentPriceText: resources.currentPriceText,
                      shippingChargesText: resources.shippingCharges,
                      priceEditorResources: {
                          ...resources
                      }
                  },
                  telemetry,
                  addToWishlistButtonText: resources.addToWishlistButtonText,
                  removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                  removeButtonText: resources.removeCartButtonText,
                  shipItText: resources.shipInsteadDisplayText,
                  pickitUpText: resources.pickItUpDisplayText,
                  changeStoreText: resources.changeStoreDisplayText,
                  storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                  outOfStockText: resources.outOfStockErrorMessage,
                  outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                  outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                  cartState: this.props.data.cart.result,
                  orgUnitLocations: this.props.data.orgUnitLocations.result,
                  removeItemClickHandler: this._removeItemFromCartHandler,
                  moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
                  updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
                  commentInputHandler: this._updateCommentInput,
                  customerInputHandler: this._updateCustomerInput,
                  requestDateInputHandler: this._updateRequestDateInput,
                  locationChangedHandler: this._fulfillmentStoreIdUpdated,
                  wishlists,
                  defaultWishlistName: resources.defaultWishlistName,
                  pickupDeliveryModeCode: PickupDeliveryModeCode,
                  includeErrors: config.showOutOfStockErrors,
                  showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                  telemetryContent: this.telemetryContent,
                  isQuantityLimitsFeatureEnabled: this.isQuantityLimitsFeatureEnabled,

                  addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                  createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                  itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                  duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                      resources
                  ),

                  addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                  addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip,
                  addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                  addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                  orderTemplates,
                  customerInformation,
                  productAvailability: this.props.data.productAvailabilites,
                  config: this.props.config
              })
            : null;
    }

    private _getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        if (MsDyn365.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnURL}`;
    }

    private readonly _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                    sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                    lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                },
                lazyload: true,
                cropFocalRegion: true
            }
        );
    };

    private readonly _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            Promise.all([
                this.props.data.checkout.result?.checkoutCart.removeCartLines(input),
                this.props.data.cart.result.removeCartLines(input)
            ])
                .then(() => {
                    this._validateCart().catch(error => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to validate Cart');
                    });
                })
                .catch(error => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to Remove Cart Line');
                });
        }
    };

    private readonly _moveToWishlistSuccessHandler = (result: IWishlistActionSuccessResult, cartline?: CartLine) => {
        if (result.status === 'ADDED' && cartline) {
            this._removeItemFromCartHandler(cartline);
        }
    };

    private readonly _updateCartlineQuantity = (cartlineToUpdate: CartLine, quantity: number, lineIndex?: number): boolean => {
        cartLineQuantityUpdatedAsync(
            this.isQuantityLimitsFeatureEnabled,
            this.props.data.cart.result,
            this.props.telemetry,
            this.state.products,
            cartlineToUpdate,
            quantity
        )
            .then((cartActionResult: ICartActionResult | undefined): boolean => {
                if (cartActionResult?.status === 'SUCCESS') {
                    this._validateCart().catch(error => {
                        this.props.telemetry.warning((error as Error).message);
                        this.props.telemetry.debug('Unable to validate Cart');
                    });

                    return true;
                }

                if (!ObjectExtensions.isNullOrUndefined(cartActionResult)) {
                    // Show update line error message.
                    const lineUpdateFailure: LineDataValidationFailure = {
                        LineIndex: ObjectExtensions.isNullOrUndefined(lineIndex) ? 0 : lineIndex,
                        DataValidationFailure: {
                            ErrorContext: ArrayExtensions.hasElements(cartActionResult.validationResults)
                                ? cartActionResult.validationResults[0].ErrorContext
                                : '',
                            LocalizedMessage: cartActionResult.errorDetails?.LocalizedMessage
                        }
                    };

                    this.setState((previousState: Readonly<ICartComponentState>) => {
                        const currentErrors: LineDataValidationFailure[] = previousState.cartlinesErrors.filter(
                            error => error.LineIndex !== lineIndex
                        );
                        currentErrors.push(lineUpdateFailure);
                        return { cartlinesErrors: currentErrors };
                    });
                }

                return false;
            })
            .catch(() => {
                return false;
            });

        return false;
    };

    private readonly _updateCommentInput = (cartlineToUpdate: any, comment: string, lineIndex: number): void => {
        const cartLines: any = this.props.data.cart.result;
        const index = cartlineToUpdate.ExtensionProperties.findIndex((e: any) => {
            return e.Key === 'AAA_RetailItemNotes';
        });
        if (index > -1) {
            cartlineToUpdate.ExtensionProperties[index] = {
                Key: 'AAA_RetailItemNotes',
                Value: {
                    StringValue: comment
                }
            };
        } else {
            cartlineToUpdate.ExtensionProperties.push({
                Key: 'AAA_RetailItemNotes',
                Value: {
                    StringValue: comment
                }
            });
        }

        cartLines[lineIndex] = cartlineToUpdate;
        this.setState({});
    };
    private readonly _updateCustomerInput = (field: string, cartlineToUpdate: any, value: string, lineIndex: number): void => {
        const cartLines: any = this.props.data.cart.result;

        let key = '';
        let updatedValue = value;
        if (field === 'name') {
            key = 'AAA_B2CCUSTOMERNAME';
        }
        if (field === 'email') {
            key = 'AAA_B2CEMAIL';
        }
        if (field === 'phone') {
            key = 'AAA_B2CPHONE';
            const isnum = /^[0-9]+$/.test(value);
            if (!value || isnum) {
                updatedValue = value;
            } else {
                const index = cartlineToUpdate.ExtensionProperties.findIndex((e: any) => {
                    return e.Key === key;
                });
                if (index > -1) {
                    updatedValue = cartlineToUpdate.ExtensionProperties[index].Value.StringValue;
                } else {
                    updatedValue = '';
                }
            }
        }
        const index = cartlineToUpdate.ExtensionProperties.findIndex((e: any) => {
            return e.Key === key;
        });
        if (index > -1) {
            cartlineToUpdate.ExtensionProperties[index] = {
                Key: key,
                Value: {
                    StringValue: updatedValue
                }
            };
        } else {
            cartlineToUpdate.ExtensionProperties.push({
                Key: key,
                Value: {
                    StringValue: updatedValue
                }
            });
        }
        cartLines[lineIndex] = cartlineToUpdate;
        this.setState({});
    };

    private readonly _updateRequestDateInput = (cartlineToUpdate: any, date: string, lineIndex: number): void => {
        const cartLines: any = this.props.data.cart.result;
        const index = cartlineToUpdate.ExtensionProperties.findIndex((e: any) => {
            return e.Key === 'AAA_REQUIREDDATE';
        });
        const currentDateString = moment(new Date()).format('DD-MM-YYYY');
        let days = 21;
        if (this.props.config.minRequiredDateDays === undefined) {
            days = 21;
        } else if (isNaN(this.props.config.minRequiredDateDays) || this.props.config.minRequiredDateDays < 0) {
            days = 0;
        } else {
            days = this.props.config.minRequiredDateDays;
        }
        const newDate = moment(currentDateString, 'DD-MM-YYYY').add('days', days);
        const minDateToMap = moment(newDate).format('YYYY-MM-DD');
        let dateValue = date && moment(date).format('YYYY-MM-DD');
        dateValue = date ? `${dateValue}T00:00:00Z` : `${minDateToMap}T00:00:00Z`;
        if (index > -1) {
            cartlineToUpdate.ExtensionProperties[index] = {
                Key: 'AAA_REQUIREDDATE',
                Value: {
                    StringValue: dateValue
                }
            };
        } else {
            cartlineToUpdate.ExtensionProperties.push({
                Key: 'AAA_REQUIREDDATE',
                Value: {
                    StringValue: dateValue
                }
            });
        }

        cartLines[lineIndex] = cartlineToUpdate;
        this.setState({});
    };

    private readonly _fulfillmentStoreIdUpdated = () => {
        if (this.isQuantityLimitsFeatureEnabled) {
            getActiveCartProductsAction(new ActiveCartProductsInput(), this.props.context.actionContext)
                .then(products => {
                    this.setState({ products });
                })
                .then(async () => this._validateCart())
                .catch((error: Error) => {
                    this.props.telemetry.warning(error.message);
                    this.props.telemetry.debug('Unable to validate Cart');
                });
        }
    };

    private async _validateCart(): Promise<void> {
        const {
            data: { cart },
            telemetry,
            context
        } = this.props;

        const validationResults: LineDataValidationFailure[] | undefined = await validateCartAsync(
            this.isQuantityLimitsFeatureEnabled,
            cart,
            telemetry,
            context,
            true
        );

        if (validationResults) {
            const isValid = !ArrayExtensions.hasElements(validationResults);
            this.setState({
                cartlinesErrors: validationResults,
                isCartValid: isValid
            });
        } else {
            this.setState({ isCartValid: true });
        }

        return AsyncResult.resolve();
    }

    private async _getOrderQuantityLimitsFeatureFlag(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        try {
            const featureStatuses = await this.props.data.featureState;
            const isQuantityLimitsFeatureEnabledInHq =
                featureStatuses.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                )?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) {
                return false;
            }
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get feature states');
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        try {
            const customerInfo = await this.props.data.customerInformation;
            return (
                !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get customer info');
            return false;
        }
    }

    private _getCartLinesGroup(): ICartlinesViewProps[][] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation }
            },
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);
        const cartlinesErrors: LineDataValidationFailure[] = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }

        return this.props.data.products &&
            this.props.data.products.result &&
            cart &&
            cart.cart.CartLines &&
            cart.cart.CartLines.length > 0 &&
            this.props.data.channelDeliveryOptionConfig?.result
            ? CartLineGroupItems({
                  cartLinesExtraInfo: this.state.cartLinesExtraInfo,
                  cartlines: cart.cart.CartLines,
                  storeSelectorModuleId: storeSelectorId,
                  cartlinesErrors,
                  products: this.props.data.products.result,
                  productAvailabilites: this.props.data.productAvailabilites.result,
                  productDeliveryOptions: this.props.data.deliveryOptions.result,
                  imageSettings,
                  isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                  maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                  gridSettings: this.props.context.request.gridSettings!,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  resources: {
                      sizeString: resources.productDimensionTypeSize,
                      colorString: resources.productDimensionTypeColor,
                      configString: resources.productDimensionTypeConfiguration,
                      styleString: resources.productDimensionTypeStyle,
                      amountString: resources.productDimensionTypeAmount,
                      quantityDisplayString: resources.quantityDisplayText,
                      inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                      decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                      incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                      discountStringText: resources.discountStringText,
                      originalPriceText: resources.originalPriceText,
                      currentPriceText: resources.currentPriceText,
                      shippingChargesText: resources.shippingCharges,
                      priceEditorResources: {
                          ...resources
                      }
                  },
                  telemetry,
                  addToWishlistButtonText: resources.addToWishlistButtonText,
                  removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                  removeButtonText: resources.removeCartButtonText,
                  shipItText: resources.shipInsteadDisplayText,
                  pickitUpText: resources.pickItUpDisplayText,
                  changeStoreText: resources.changeStoreDisplayText,
                  storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                  outOfStockText: resources.outOfStockErrorMessage,
                  outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                  outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                  cartState: this.props.data.cart.result,
                  orgUnitLocations: this.props.data.orgUnitLocations.result,
                  removeItemClickHandler: this._removeItemFromCartHandler,
                  moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
                  updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
                  commentInputHandler: this._updateCommentInput,
                  customerInputHandler: this._updateCustomerInput,
                  requestDateInputHandler: this._updateRequestDateInput,
                  locationChangedHandler: this._fulfillmentStoreIdUpdated,
                  wishlists,
                  defaultWishlistName: resources.defaultWishlistName,
                  pickupDeliveryModeCode: PickupDeliveryModeCode,
                  includeErrors: config.showOutOfStockErrors,
                  showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                  telemetryContent: this.telemetryContent,
                  isQuantityLimitsFeatureEnabled: this.isQuantityLimitsFeatureEnabled,

                  addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                  createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                  itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                  duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                      resources
                  ),

                  addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                  addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip,
                  addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                  addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                  orderTemplates,
                  customerInformation,
                  channelDeliveryOptionConfig: this.props.data.channelDeliveryOptionConfig?.result,
                  shouldIgnoreWarehouse: this.props.context.app.config.warehouseAggregation === 'multiple',
                  productAvailability: this.props.data.productAvailabilites,
                  config: config
              })
            : null;
    }

    // private loadBmaImages = async (snapShotId: any) => {
    //     const bmaImageEndPoint = this.props.app.config.bmaImageEndPoint.replace('#snapshotId', snapShotId);
    //     const response = await fetch(`${this.props.app.config.bmaBaseUrl}${bmaImageEndPoint}`);
    //     const images = await response.json();
    //     return images[0];
    // };
}

export default Cart;
